export type AlertType =
  | "price" | "rsi"
  | "bos" | "choch" | "cisd" | "t50" | "t50_failed" | "cisd_t50" | "fvg_created" | "fvg_filled" | "eqh" | "eql" | "liquidity_sweep"
  | "tick_dominance" | "tick_imbalance" | "tick_speed";

export type RepeatMode = "once" | "every";

export interface AlertDef {
  id: string;
  symbol: string;
  type: AlertType;
  condition: string;
  params: Record<string, number>;
  cooldownS: number;
  repeat: RepeatMode;
}

export interface AlertFire {
  alertId: string;
  type: AlertType;
  message: string;
  price: number;
  time: number; // epoch seconds of the candle that fired
  value?: number;
  payload: Record<string, unknown>;
}

export interface ParamMeta { key: string; label: string; default?: number; min?: number; max?: number; step?: number }

export interface AlertTypeMeta {
  type: AlertType;
  label: string;
  group: "Price" | "Indicators" | "Market Structure" | "Strategies" | "Felacoo Tick Engine";
  conditions: { id: string; label: string }[];
  params: ParamMeta[];
  needsTicks?: boolean; // only meaningful on tick-built candles
}

const DIR3 = [{ id: "bullish", label: "Bullish" }, { id: "bearish", label: "Bearish" }, { id: "any", label: "Any direction" }];
const CMP4 = [
  { id: "above", label: "Goes above" }, { id: "below", label: "Goes below" },
  { id: "cross_above", label: "Crosses above" }, { id: "cross_below", label: "Crosses below" },
];
const CMP2 = [{ id: "above", label: "Goes above" }, { id: "below", label: "Goes below" }];

// Single source of truth for alert types — used by the UI form, API validation and the evaluator.
export const ALERT_CATALOG: AlertTypeMeta[] = [
  { type: "price", label: "Price", group: "Price", conditions: CMP4, params: [{ key: "value", label: "Price level" }] },
  { type: "rsi", label: "RSI", group: "Indicators", conditions: CMP4, params: [{ key: "value", label: "RSI level", default: 70, min: 0, max: 100 }, { key: "period", label: "Period", default: 14, min: 2 }] },
  { type: "bos", label: "Break of Structure", group: "Market Structure", conditions: DIR3, params: [] },
  { type: "choch", label: "Change of Character", group: "Market Structure", conditions: DIR3, params: [] },
  { type: "cisd", label: "CISD confirmed", group: "Strategies", conditions: DIR3, params: [] },
  { type: "t50", label: "T50 confirmed", group: "Strategies", conditions: DIR3, params: [{ key: "threshold", label: "Threshold %", default: 50, min: 50, max: 90, step: 1 }] },
  { type: "t50_failed", label: "T50 failed → counter-hedge", group: "Strategies", conditions: DIR3, params: [{ key: "threshold", label: "Threshold %", default: 50, min: 50, max: 90, step: 1 }] },
  { type: "cisd_t50", label: "CISD + T50 (same direction)", group: "Strategies", conditions: DIR3, params: [{ key: "threshold", label: "T50 threshold %", default: 50, min: 50, max: 90, step: 1 }, { key: "within", label: "CISD within bars", default: 3, min: 0, max: 20, step: 1 }] },
  { type: "fvg_created", label: "FVG created", group: "Market Structure", conditions: DIR3, params: [] },
  { type: "fvg_filled", label: "FVG filled", group: "Market Structure", conditions: DIR3, params: [] },
  { type: "eqh", label: "Equal Highs formed", group: "Market Structure", conditions: [{ id: "formed", label: "Formed" }], params: [] },
  { type: "eql", label: "Equal Lows formed", group: "Market Structure", conditions: [{ id: "formed", label: "Formed" }], params: [] },
  { type: "liquidity_sweep", label: "Liquidity sweep", group: "Market Structure", conditions: DIR3, params: [] },
  { type: "tick_dominance", label: "Tick Dominance", group: "Felacoo Tick Engine", conditions: CMP2, params: [{ key: "value", label: "% up-ticks", default: 65, min: 0, max: 100 }, { key: "period", label: "Period", default: 20, min: 1 }], needsTicks: true },
  { type: "tick_imbalance", label: "Tick Imbalance", group: "Felacoo Tick Engine", conditions: CMP2, params: [{ key: "value", label: "Net ticks", default: 10 }, { key: "period", label: "Period", default: 20, min: 1 }], needsTicks: true },
  { type: "tick_speed", label: "Tick Speed", group: "Felacoo Tick Engine", conditions: CMP2, params: [{ key: "value", label: "Ticks / second", default: 1.5, step: 0.1 }, { key: "smooth", label: "Smoothing", default: 5, min: 1 }], needsTicks: true },
];

export function alertMeta(type: string): AlertTypeMeta | undefined {
  return ALERT_CATALOG.find((a) => a.type === type);
}

export function validateAlert(a: Partial<AlertDef>): string | null {
  const meta = alertMeta(a.type ?? "");
  if (!meta) return `Unknown alert type: ${a.type}`;
  if (!meta.conditions.some((c) => c.id === a.condition)) return `Invalid condition '${a.condition}' for ${meta.label}`;
  for (const p of meta.params) {
    const v = a.params?.[p.key] ?? p.default;
    if (v == null || !isFinite(v)) return `Missing parameter: ${p.label}`;
  }
  return null;
}
